import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { ProductContext } from "../../store";
import {
	listAiVisibilityAudits,
	saveAiVisibilityStore,
	cancelAiVisibilityAudit,
	LIST_POLL_MS,
} from "../../services/aiVisibilityApi";
import { formatDistanceToNow, isAuditInProgress } from "./auditUtils";
import { getAuditGradePillClass } from "./gradeColors";
import { getActiveAuditIdFromWebsite } from "./websiteKeysUtils";
import AuditStatusBadge from "./AuditStatusBadge";
import AuditHighlightsPanel from "./AuditHighlightsPanel";
import JobsListPagination from "./JobsListPagination";
import {
	AI_VISIBILITY_LIST_PAGE_SIZE,
	getListRangeLabel,
} from "./aiVisibilityListConstants";
import { auditIdsMatch } from "../../utils/auditIdUtils";

const AuditHistorySection = ({ showActions = true, embedded = false }) => {
	const navigate = useNavigate();
	const { currentWebsiteId, websiteData } = useContext(ProductContext);
	const [audits, setAudits] = useState([]);
	const [total, setTotal] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [expandedAuditId, setExpandedAuditId] = useState(null);
	const [actionLoading, setActionLoading] = useState(false);
	const intervalRef = useRef(null);

	const activeAuditId = getActiveAuditIdFromWebsite(websiteData?.[currentWebsiteId]);

	useEffect(() => {
		setPage(0);
	}, [currentWebsiteId]);

	const fetchAudits = useCallback(async () => {
		if (!currentWebsiteId) {
			setAudits([]);
			setTotal(0);
			setHasMore(false);
			setLoading(false);
			return;
		}
		const result = await listAiVisibilityAudits(
			currentWebsiteId,
			page * AI_VISIBILITY_LIST_PAGE_SIZE,
			AI_VISIBILITY_LIST_PAGE_SIZE
		);
		if (!result.ok) {
			setError(result.message);
			setAudits([]);
			setTotal(0);
			setHasMore(false);
		} else {
			setError(null);
			const nextAudits = result.audits || [];
			setAudits(nextAudits);
			setTotal(result.total ?? nextAudits.length);
			setHasMore(result.hasMore ?? false);
		}
		setLoading(false);
	}, [currentWebsiteId, page]);

	useEffect(() => {
		setLoading(true);
		fetchAudits();
	}, [fetchAudits]);

	useEffect(() => {
		if (!currentWebsiteId) return undefined;
		const needsPoll = audits.some((a) => isAuditInProgress(a.status));
		if (!needsPoll) {
			if (intervalRef.current) clearInterval(intervalRef.current);
			return undefined;
		}
		intervalRef.current = setInterval(fetchAudits, LIST_POLL_MS);
		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, [audits, currentWebsiteId, fetchAudits]);

	const handleRerun = async () => {
		const website = websiteData?.[currentWebsiteId];
		const storeURL = website?.websiteURL || website?.websiteUrl || "";

		if (!storeURL) {
			toast.error("Website URL not found");
			return;
		}

		setActionLoading(true);
		const result = await saveAiVisibilityStore(storeURL, website?.websiteType, {
			enable_web_search: true,
			prompt_count: 15,
		});
		setActionLoading(false);
		if (!result.ok) {
			toast.error(result.message);
			return;
		}
		toast.success("Audit rerun started");
		fetchAudits();
	};

	const handleCancel = async () => {
		setActionLoading(true);
		const result = await cancelAiVisibilityAudit(currentWebsiteId);
		setActionLoading(false);
		if (!result.ok) {
			toast.error(result.message);
			return;
		}
		toast.success("Audit cancelled");
		fetchAudits();
	};

	if (!currentWebsiteId) return null;

	return (
		<Card className="shadow-sm ai-visibility-audit-history">
			{!embedded && (
				<CardHeader>
					<h5 className="mb-1">Audit History</h5>
					<p className="text-muted small mb-0">
						{loading
							? "Loading…"
							: `${total} audit${total !== 1 ? "s" : ""} total · ${getListRangeLabel(
									page,
									AI_VISIBILITY_LIST_PAGE_SIZE,
									total
							  )}`}
					</p>
				</CardHeader>
			)}
			<CardBody>
				{loading ? (
					<div className="text-center py-5">
						<Spinner />
					</div>
				) : (
					<>
				{error && <p className="text-danger small">{error}</p>}
				{!error && audits.length === 0 && (
					<p className="text-muted text-center py-4 mb-0">
						No audits yet. Complete onboarding to start your first visibility audit.
					</p>
				)}
				<div className="d-flex flex-column gap-3">
					{audits.map((audit) => {
						const inProgress = isAuditInProgress(audit.status);
						const isCompleted = String(audit.status).toLowerCase() === "completed";
						const isFailed = String(audit.status).toLowerCase() === "failed";
						const isActive =
							activeAuditId != null && auditIdsMatch(audit.id, activeAuditId);
						const summary = isCompleted ? audit.summary : null;
						const showMetrics =
							isCompleted &&
							summary?.overall_score != null &&
							summary?.grade != null &&
							summary.grade !== "";
						const displayTimestamp = isCompleted
							? audit.completed_at || audit.created_at
							: audit.created_at;

						return (
							<div
								key={audit.id}
								className={`border rounded overflow-hidden ${
									isActive ? "border-primary" : ""
								}`}
							>
								<div
									className={`audit-history-row-header d-flex flex-wrap align-items-start justify-content-between gap-2 p-3 ${
										isActive ? "bg-light" : ""
									}`}
								>
									<div className="flex-grow-1 min-w-0">
										<div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
											<AuditStatusBadge status={audit.status} />
											<span className="text-muted small">
												{formatDistanceToNow(displayTimestamp)}
											</span>
											{isActive && (
												<span className="badge bg-primary">Current</span>
											)}
										</div>
										<a
											href={audit.url}
											target="_blank"
											rel="noopener noreferrer"
											className="text-break"
										>
											{audit.url}
											<i className="fas fa-external-link-alt ms-1 small" />
										</a>
										{showMetrics && (
											<div className="audit-history-metrics mt-2">
												<span className="audit-history-metrics__item">
													<span className="audit-history-metrics__label">Score:</span>{" "}
													<strong className="audit-history-metrics__value">
														{summary.overall_score.toFixed(1)}/100
													</strong>
												</span>
												<span className="audit-history-metrics__item">
													<span className="audit-history-metrics__label">Grade:</span>{" "}
													<span
														className={`audit-history-grade-pill ${getAuditGradePillClass(summary.grade)}`}
													>
														{summary.grade}
													</span>
												</span>
												{summary.grade_label && (
													<span className="audit-history-metrics__label">
														{summary.grade_label}
													</span>
												)}
											</div>
										)}
										{audit.error_message && (
											<p className="text-danger small mt-2 mb-0">{audit.error_message}</p>
										)}
									</div>
									{showActions && (
										<div className="d-flex flex-wrap align-items-center gap-2">
											{inProgress && (
												<Button
													color="danger"
													outline
													size="sm"
													disabled={actionLoading}
													onClick={handleCancel}
												>
													<i className="fas fa-times me-1" />
													Cancel
												</Button>
											)}
											{(isFailed || String(audit.status).toLowerCase() === "analyzing") && (
												<Button
													color="primary"
													outline
													size="sm"
													disabled={actionLoading}
													onClick={handleRerun}
												>
													<i className="fas fa-redo me-1" />
													Rerun
												</Button>
											)}
											{isCompleted && (
												<>
													<Button
														color="primary"
														size="sm"
														onClick={() =>
															navigate(`/aivisibility/report?auditId=${audit.id}`)
														}
													>
														<i className="fas fa-file-alt me-1" />
														View Report
													</Button>
													<Button
														color="link"
														size="sm"
														className="text-decoration-none text-primary fw-medium p-0"
														onClick={() =>
															setExpandedAuditId(
																expandedAuditId === audit.id ? null : audit.id
															)
														}
													>
														{expandedAuditId === audit.id ? (
															<>
																Hide Details
																<i className="fas fa-chevron-up ms-1" />
															</>
														) : (
															<>
																View Details
																<i className="fas fa-chevron-down ms-1" />
															</>
														)}
													</Button>
													<Button
														color="primary"
														outline
														size="sm"
														disabled={actionLoading}
														onClick={handleRerun}
													>
														<i className="fas fa-redo me-1" />
														Rerun
													</Button>
												</>
											)}
										</div>
									)}
								</div>
								{showActions && expandedAuditId === audit.id && isCompleted && (
									<div className="audit-history-highlights-panel">
										<AuditHighlightsPanel
											websiteId={currentWebsiteId}
											auditId={audit.id}
										/>
									</div>
								)}
							</div>
						);
					})}
				</div>
				<JobsListPagination
					page={page}
					pageSize={AI_VISIBILITY_LIST_PAGE_SIZE}
					total={total}
					hasMore={hasMore}
					onPageChange={setPage}
				/>
					</>
				)}
			</CardBody>
		</Card>
	);
};

export default AuditHistorySection;
